import {customElement, property, query, queryAll, state} from "lit/decorators.js";
import {UnLitElement, WithEopSlots} from "../../../common/elements";
import {html, type PropertyValues, type TemplateResult} from "lit";
import type {Nullable} from "../../../bootstrap/common/types";
import {eventOccurredOutside, handleActivationKey, KEYS} from "../../../common/utils/events";
import {Dictionary} from "../dictionary";
import {EventBus} from "../../../common/eventBus";
import {resolve} from "../../../container";
import {Page} from "../../../common/page";
import {ClickTracking} from "../../../tracking/clickTracking";
import {ResizeObserverFactory} from "../../../common/observation";

export const DIALOG_OPENED_EVENT = "dialogOpened";
export const DIALOG_CLOSED_EVENT = "dialogClosed";

export const CLOSE_DIALOG = "closeDialog";

const PANEL_SIZE_FACTOR = 1.5;

@customElement("eop-dialog")
export class EopDialog extends WithEopSlots(UnLitElement) {

    @queryAll(".layer-section")
    private layerSections: NodeListOf<HTMLElement>;
    @queryAll(".close")
    private closeButtons: NodeListOf<HTMLButtonElement>;
    @query("dialog")
    private dialog: HTMLDialogElement;
    @query(".dialog-panel")
    private panel: HTMLElement;
    @property({attribute: "disable-close-on-outside-click", type: Boolean})
    public disableCloseOnOutsideClick: boolean = false;
    @property({attribute: "content-slot-name"})
    public contentSlotName: string;
    @property({attribute: "popovers-slot-name"})
    public popoversSlotName: string;
    @state()
    private showSecondaryCloseButton: boolean = false;

    private resizeObserver: ResizeObserver;

    private dictionary: Dictionary;

    public constructor(
        private eventBus: EventBus = resolve(EventBus),
        private page: Page = resolve(Page),
        private clickTracking: ClickTracking = resolve(ClickTracking),
        private resizeObserverFactory: ResizeObserverFactory = resolve(ResizeObserverFactory)
    ) {
        super();

        this.dictionary = Dictionary.of(this);
        this.addEventListener(CLOSE_DIALOG, () => this.close());
    }

    public render(): TemplateResult {
        return html`
            <dialog closedby="none"
                    @click=${this.closeOnOutsideClick}
                    @keydown=${this.closeOnEscape}
                    class="vertically-scrollable-area"
            >
                <div class="dialog-container">
                    <div class="dialog-panel">
                        <button type="button"
                                class="close-button"
                                aria-label=${this.dictionary.translate("MSG_CLOSE_DIALOG")}
                                @click=${this.closeWithTracking}
                        ><eop-svg-sprite-icon icon-id="close"></eop-svg-sprite-icon></button>
                        ${this.resolveSlot(this.contentSlotName)}
                        ${this.renderSecondaryCloseButton()}
                    </div>
                    <eop-tab-out-listener .callback=${() => this.close()}></eop-tab-out-listener>
                </div>
                ${this.resolveSlot(this.popoversSlotName)}
            </dialog>
        `;
    }

    public isOpen(): boolean {
        return this.dialog ? this.dialog.open : false;
    }

    public connectedCallback(): void {
        super.connectedCallback();

        this.resizeObserver = this.resizeObserverFactory.create(() => this.updateSecondaryCloseButtonVisibility());
    }

    public disconnectedCallback(): void {
        this.resizeObserver.disconnect();
        super.disconnectedCallback();
    }

    private updateSecondaryCloseButtonVisibility(): void {
        this.showSecondaryCloseButton = this.panel.clientHeight > this.page.viewportHeight() * PANEL_SIZE_FACTOR;
    }

    private renderSecondaryCloseButton(): Nullable<TemplateResult> {
        if (!this.showSecondaryCloseButton) {
            return null;
        }
        return html`
            <div class="bottom-close-section layer-section ${this.backgroundClassFromSectionAbove()}">
                <button class="secondary" @click=${this.closeWithTracking}>
                    <eop-msg key="MSG_CLOSE_DIALOG"></eop-msg>
                </button>
            </div>
        `;
    }

    protected firstUpdated(_changedProperties: PropertyValues): void {
        super.firstUpdated(_changedProperties);
        Array.from(this.closeButtons).forEach(button => {
            button.addEventListener("click", event => this.closeWithTracking(event));
            button.addEventListener("keydown", (e: KeyboardEvent) => {
                handleActivationKey(e, () => this.closeWithTracking(e));
            });
        });
        this.resizeObserver.observe(this.panel);
    }

    public showModal(): void {
        this.dialog.showModal();
        this.eventBus.dispatchEvent(DIALOG_OPENED_EVENT, this);
    }

    public close(): void {
        this.dialog.close();
        this.eventBus.dispatchEvent(DIALOG_CLOSED_EVENT, this);
    }

    private closeWithTracking(event: Event): void {
        this.clickTracking.handleButtonInteract(event, "dialog-close");
        this.close();
    }

    private closeOnOutsideClick(e: Event): void {
        if (!this.disableCloseOnOutsideClick && eventOccurredOutside(e, this.panel)) {
            this.closeWithTracking(e);
        }
    }

    private closeOnEscape(e: KeyboardEvent): void {
        if (e.key === KEYS.ESCAPE) {
            this.closeWithTracking(e);
        }
    }

    private backgroundClassFromSectionAbove(): string {
        if (this.layerSections.length <= 0) {
            return "";
        }

        const classes = this.layerSections.item(this.layerSections.length - 1).classList.values();
        for (const className of classes) {
            if (className.includes("background-")) {
                return className;
            }
        }
        return "";
    }

}